// src/components/ConversationDetailPage.js
import React, { useState, useEffect, useRef } from 'react';
import { useWebSocket } from '../contexts/WebSocketContext';
import { useParams, Link as RouterLink } from 'react-router-dom';
import axiosInstance from '../api/axiosInstance';
import { motion } from 'framer-motion';

// ✨ NEW: استيراد مكونات إضافية من Material-UI
import { Container, Typography, Box, Paper, CircularProgress, Alert, Button, TextField, IconButton, Chip, Avatar, Divider, Tooltip, Badge, Fade, Dialog } from '@mui/material';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import SendIcon from '@mui/icons-material/Send';
import PersonIcon from '@mui/icons-material/Person';
import SmartToyIcon from '@mui/icons-material/SmartToy';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import RefreshIcon from '@mui/icons-material/Refresh';
import PauseIcon from '@mui/icons-material/Pause';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';

import InfoIcon from '@mui/icons-material/Info';
import ChatBubbleOutlineIcon from '@mui/icons-material/ChatBubbleOutline';

const pageVariants = {
  initial: { opacity: 0, x: "-5vw" },
  in: { opacity: 1, x: 0 },
  out: { opacity: 0, x: "5vw" }
};

const pageTransition = {
  type: "tween",
  ease: "anticipate",
  duration: 0.5
};

// ✨ NEW: مكون رسالة محسّن
const MessageBubble = ({ message, onImageClick }) => {
  const isUser = message.sender_type === 'user';
  const isAgent = ['agent', 'human', 'admin'].includes(message.sender_type);
  const avatarColor = isUser ? 'primary.main' : isAgent ? 'warning.main' : 'secondary.main';
  const badgeColor = isUser ? 'success.main' : isAgent ? 'warning.main' : 'primary.main';
  const bubbleBg = isUser ? 'primary.main' : isAgent ? '#fff7ed' : 'white';
  const bubbleColor = isUser ? 'primary.contrastText' : 'text.primary';

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      transition={{
        duration: 0.2
      }}
    >
      <Box sx={{
        display: 'flex',
        justifyContent: isUser ? 'flex-end' : 'flex-start',
        mb: 2,
        px: 1
      }}>
        <Box sx={{
          display: 'flex',
          alignItems: 'flex-end',
          gap: 1,
          maxWidth: '75%',
          flexDirection: isUser ? 'row-reverse' : 'row'
        }}>
          <Badge
            overlap="circular"
            anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
            badgeContent={
              <Box sx={{
                width: 12,
                height: 12,
                borderRadius: '50%',
                bgcolor: badgeColor,
                border: '2px solid white',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)'
              }} />
            }
          >
            <Avatar
              sx={{
                width: 36,
                height: 36,
                bgcolor: avatarColor,
                fontSize: '0.875rem',
                boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                '&:hover': {
                  transform: 'scale(1.1)',
                  transition: 'transform 0.2s ease'
                }
              }}
            >
              {isUser ? <PersonIcon /> : isAgent ? <SupportAgentIcon /> : <SmartToyIcon />}
            </Avatar>
          </Badge>

          <Paper
            elevation={3}
            sx={{
              p: 2.5,
              bgcolor: bubbleBg,
              color: bubbleColor,
              borderRadius: isUser ? '20px 20px 6px 20px' : '20px 20px 20px 6px',
              maxWidth: '100%',
              wordBreak: 'break-word',
              boxShadow: isUser
                ? '0 6px 20px rgba(14, 165, 233, 0.25)'
                : '0 4px 16px rgba(0,0,0,0.08)',
              border: isUser ? 'none' : '1px solid',
              borderColor: 'divider',
              '&:hover': {
                boxShadow: isUser
                  ? '0 8px 24px rgba(14, 165, 233, 0.35)'
                  : '0 6px 20px rgba(0,0,0,0.12)',
                transform: 'translateY(-1px)',
                transition: 'all 0.2s ease'
              },
              position: 'relative',
              '&::before': isUser ? {
                content: '""',
                position: 'absolute',
                bottom: 0,
                right: -8,
                width: 0,
                height: 0,
                borderLeft: '8px solid',
                borderLeftColor: 'primary.main',
                borderBottom: '8px solid transparent'
              } : isAgent ? {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: -8,
                width: 0,
                height: 0,
                borderRight: '8px solid',
                borderRightColor: '#fff7ed',
                borderBottom: '8px solid transparent'
              } : {
                content: '""',
                position: 'absolute',
                bottom: 0,
                left: -8,
                width: 0,
                height: 0,
                borderRight: '8px solid',
                borderRightColor: 'white',
                borderBottom: '8px solid transparent'
              }
            }}
          >
            <Typography
              variant="body1"
              component="div"
              sx={{
                whiteSpace: 'pre-wrap',
                lineHeight: 1.6,
                fontSize: '0.95rem',
                fontWeight: isUser ? 500 : 400
              }}
            >
              {message.media_type && message.media_type !== 'text' && message.media_url ? (
                <Box sx={{ mt: 1, mb: 1 }}>
                  {message.media_type === 'image' && (
                    <img
                      src={message.media_url}
                      alt={message.media_caption || 'Image'}
                      onClick={() => onImageClick && onImageClick(message.media_url)}
                      style={{
                        maxWidth: '100%',
                        borderRadius: '8px',
                        maxHeight: '300px',
                        cursor: 'pointer',
                        transition: 'transform 0.2s',
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.02)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    />
                  )}
                  {message.media_type === 'video' && (
                    <video
                      controls
                      src={message.media_url}
                      style={{ maxWidth: '100%', borderRadius: '8px', maxHeight: '300px' }}
                    />
                  )}
                  {message.media_type === 'audio' && (
                    <audio
                      controls
                      src={message.media_url}
                      style={{ maxWidth: '100%' }}
                    />
                  )}
                  {message.media_type === 'document' && (
                    <Button
                      href={message.media_url}
                      target="_blank"
                      variant="outlined"
                      size="small"
                      startIcon={<span role="img" aria-label="doc">📄</span>}
                      sx={{ color: isUser ? 'inherit' : 'primary.main', borderColor: isUser ? 'rgba(255,255,255,0.5)' : 'inherit' }}
                    >
                      {message.media_caption || 'تحميل الملف'}
                    </Button>
                  )}
                  {message.media_caption && message.media_type !== 'text' && (
                    <Typography variant="caption" display="block" sx={{ mt: 0.5 }}>
                      {message.media_caption}
                    </Typography>
                  )}
                </Box>
              ) : (
                message.content
              )}
            </Typography>
            <Typography
              variant="caption"
              sx={{
                display: 'block',
                textAlign: isUser ? 'left' : 'right',
                opacity: 0.7,
                mt: 1.5,
                fontSize: '0.75rem',
                fontWeight: 500
              }}
            >
              {new Date(message.timestamp).toLocaleTimeString('ar-SA', {
                hour: '2-digit',
                minute: '2-digit'
              })}
            </Typography>
          </Paper>
        </Box>
      </Box>
    </motion.div>
  );
};

export default function ConversationDetailPage() {
  const [conversation, setConversation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const { id } = useParams();
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [refreshing, setRefreshing] = useState(false);
  const [showInfo, setShowInfo] = useState(false);
  const [selectedImage, setSelectedImage] = useState(null); // ✨ NEW: حالة الصورة المختارة
  const [togglingStatus, setTogglingStatus] = useState(false); // ✨ NEW: حالة جاري التغيير
  const messagesEndRef = useRef(null);

  // حالة لتخزين الحالة الحالية للمحادثة وتحديثها
  const [currentStatus, setCurrentStatus] = useState('active_bot');

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    const fetchConversationDetails = async () => {
      try {
        setLoading(true);
        setError('');
        const response = await axiosInstance.get(`/chatbot/conversations/${id}/`);
        const status = response.data.status;
        setConversation(response.data);
        setCurrentStatus(status);
      } catch (err) {
        if (err.response?.status !== 401) {
          setError("لا يمكن تحميل تفاصيل المحادثة.");
        }
      } finally {
        setLoading(false);
      }
    };
    fetchConversationDetails();
  }, [id]);

  // ⚡ تحديث لحظي: رسالة جديدة في المحادثة المفتوحة تظهر فوراً
  const { lastMessage } = useWebSocket() || {};
  useEffect(() => {
    if (lastMessage?.type !== 'new_message') return;
    if (String(lastMessage.conversation_id) !== String(id)) return;
    (async () => {
      try {
        const response = await axiosInstance.get(`/chatbot/conversations/${id}/`);
        setConversation(response.data);
        setCurrentStatus(response.data.status);
      } catch (e) { /* silent */ }
    })();
  }, [lastMessage, id]);

  useEffect(() => {
    scrollToBottom();
  }, [conversation?.messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim() || sending) return;
    setSending(true);
    try {
      // sender_type 'agent' يفعّل: التعلم الذاتي + إيقاف البوت تلقائياً عند رد الموظف
      const response = await axiosInstance.post(`/chatbot/conversations/${id}/send/`, { content: newMessage, sender_type: 'agent' });
      setConversation(prev => ({ ...prev, messages: [...prev.messages, response.data] }));
      setNewMessage('');
    } catch (err) {
      console.error("Failed to send message:", err);
      alert("فشل إرسال الرسالة.");
    } finally {
      setSending(false);
    }
  };

  // دالة لتحديث المحادثة
  const handleRefresh = async () => {
    setRefreshing(true);
    try {
      const response = await axiosInstance.get(`/chatbot/conversations/${id}/`);
      const status = response.data.status;
      setConversation(response.data);
      setCurrentStatus(status);
    } catch (err) {
      console.error("Failed to refresh conversation:", err);
    } finally {
      setRefreshing(false);
    }
  };

  // ✨ NEW: دالة تبديل حالة البوت
  const handleToggleBot = async () => {
    setTogglingStatus(true);
    try {
      const response = await axiosInstance.post(`/chatbot/conversations/${id}/toggle-bot/`);
      if (response.data.success) {
        setCurrentStatus(response.data.status);
        // التحديث التلقائي للمصادر إذا لزم الأمر
        if (response.data.action === "resumed") {
          handleRefresh();
        }
      }
    } catch (err) {
      console.error("Failed to toggle bot status:", err);
      alert("فشل تغيير حالة البوت.");
    } finally {
      setTogglingStatus(false);
    }
  };

  // دالة لتنسيق التاريخ
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    return date.toLocaleDateString('ar-SA', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  // دالة لتحديد لون الحالة
  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case 'active':
      case 'نشط':
      case 'active_bot':
        return 'success';
      case 'pending':
      case 'في الانتظار':
        return 'warning';
      case 'completed':
      case 'مكتمل':
      case 'closed':
        return 'info';
      case 'active_human':
        return 'secondary';
      default:
        return 'default';
    }
  };

  // دالة لتحديد نص الحالة
  const getStatusText = (status) => {
    switch (status?.toLowerCase()) {
      case 'active_bot':
        return 'نشطة مع البوت';
      case 'active_human':
        return 'محولة للدعم البشري';
      case 'closed':
        return 'مغلقة';
      default:
        return status || 'غير محدد';
    }
  };

  if (loading) {
    return (
      <Box sx={{
        display: 'flex',
        justifyContent: 'center',
        alignItems: 'center',
        height: 'calc(100vh - 64px)',
        flexDirection: 'column',
        gap: 2
      }}>
        <CircularProgress size={60} />
        <Typography variant="h6" color="text.secondary">
          جاري تحميل المحادثة...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Container maxWidth="md" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ borderRadius: 2 }}>
          {error}
        </Alert>
      </Container>
    );
  }

  if (!conversation) {
    return (
      <Container maxWidth="md" sx={{ mt: 4, textAlign: 'center' }}>
        <Typography variant="h5" color="text.secondary">
          لم يتم العثور على بيانات المحادثة.
        </Typography>
      </Container>
    );
  }

  return (
    <motion.div
      initial="initial"
      animate="in"
      exit="out"
      variants={pageVariants}
      transition={pageTransition}
      style={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 64px)' }}
    >
      <Container maxWidth="lg" sx={{ display: 'flex', flexDirection: 'column', flexGrow: 1 }}>
        {/* Header */}
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2, mb: 3 }}>
          <Button
            component={RouterLink}
            to="/conversations"
            startIcon={<ArrowBackIcon />}
            variant="outlined"
            sx={{
              borderRadius: 2,
              px: 3,
              '&:hover': {
                transform: 'translateX(-2px)',
                boxShadow: '0 4px 12px rgba(0,0,0,0.15)'
              },
              transition: 'all 0.2s ease'
            }}
          >
            العودة للقائمة
          </Button>

          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Tooltip title="معلومات المحادثة">
              <IconButton
                onClick={() => setShowInfo(!showInfo)}
                sx={{
                  bgcolor: showInfo ? 'primary.main' : 'primary.50',
                  color: showInfo ? 'white' : 'primary.main',
                  '&:hover': {
                    bgcolor: showInfo ? 'primary.dark' : 'primary.100'
                  },
                  transition: 'all 0.2s ease'
                }}
              >
                <InfoIcon />
              </IconButton>
            </Tooltip>

            <Tooltip title="تحديث المحادثة">
              <IconButton
                onClick={handleRefresh}
                disabled={refreshing}
                sx={{
                  bgcolor: 'primary.50',
                  '&:hover': { bgcolor: 'primary.100' },
                  transition: 'all 0.2s ease'
                }}
              >
                <RefreshIcon sx={{
                  transform: refreshing ? 'rotate(360deg)' : 'rotate(0deg)',
                  transition: 'transform 1s linear'
                }} />
              </IconButton>
            </Tooltip>

            <Tooltip title={currentStatus === 'active_bot' ? "إيقاف البوت مؤقتاً" : "تشغيل البوت"}>
              <Button
                variant="contained"
                size="small"
                onClick={handleToggleBot}
                disabled={togglingStatus}
                color={currentStatus === 'active_bot' ? "warning" : "success"}
                startIcon={togglingStatus ? <CircularProgress size={16} color="inherit" /> : (currentStatus === 'active_bot' ? <PauseIcon /> : <PlayArrowIcon />)}
                sx={{ borderRadius: 2, fontWeight: 600 }}
              >
                {currentStatus === 'active_bot' ? "إيقاف البوت" : "تشغيل البوت"}
              </Button>
            </Tooltip>

            <Chip
              label={getStatusText(currentStatus)}
              color={getStatusColor(currentStatus)}
              sx={{ borderRadius: 2, fontWeight: 600 }}
            />
          </Box>
        </Box>

        {/* Conversation Info - Collapsible */}
        <Fade in={showInfo}>
          <Paper sx={{
            p: 3,
            mb: 3,
            borderRadius: 3,
            boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
            border: '1px solid',
            borderColor: 'divider',
            background: 'linear-gradient(135deg, #f8fafc 0%, #f1f5f9 100%)'
          }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Avatar sx={{
                  bgcolor: 'primary.main',
                  width: 56,
                  height: 56,
                  boxShadow: '0 4px 12px rgba(14, 165, 233, 0.3)'
                }}>
                  <PersonIcon />
                </Avatar>
                <Box>
                  <Typography variant="h5" fontWeight="700" color="primary">
                    {conversation.user_phone_number || 'مستخدم غير معروف'}
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                    <ChatBubbleOutlineIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
                    <Typography variant="body2" color="text.secondary">
                      {conversation.messages?.length || 0} رسالة
                    </Typography>
                  </Box>
                </Box>
              </Box>
              <Chip
                label={getStatusText(currentStatus)}
                color={getStatusColor(currentStatus)}
                variant="filled"
                size="large"
                sx={{
                  fontWeight: 600,
                  fontSize: '0.875rem',
                  px: 2
                }}
              />
            </Box>

            <Divider sx={{ my: 2 }} />

            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <AccessTimeIcon sx={{ fontSize: 18, color: 'text.disabled' }} />
              <Typography variant="body2" color="text.secondary" fontWeight={500}>
                بدأت في {formatDate(conversation.created_at)}
              </Typography>
            </Box>
          </Paper>
        </Fade>

        {/* Messages Area */}
        <Paper sx={{
          flexGrow: 1,
          overflowY: 'auto',
          p: 3,
          mb: 3,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          bgcolor: 'grey.50',
          border: '1px solid',
          borderColor: 'divider',
          position: 'relative',
          '&::before': {
            content: '""',
            position: 'absolute',
            top: 0,
            left: 0,
            right: 0,
            height: '1px',
            background: 'linear-gradient(90deg, transparent, rgba(0,0,0,0.1), transparent)'
          }
        }}>
          {conversation.messages && conversation.messages.length > 0 ? (
            <Box sx={{ pb: 2 }}>
              {conversation.messages.map((msg, index) => (
                <MessageBubble
                  key={msg.id}
                  message={msg}
                  index={index}
                  onImageClick={setSelectedImage}
                />
              ))}
            </Box>
          ) : (
            <Box sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '100%',
              textAlign: 'center',
              color: 'text.secondary',
              py: 8
            }}>
              <Box sx={{
                p: 3,
                borderRadius: '50%',
                bgcolor: 'primary.50',
                mb: 3,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center'
              }}>
                <SmartToyIcon sx={{ fontSize: 48, color: 'primary.main' }} />
              </Box>
              <Typography variant="h5" gutterBottom fontWeight={600}>
                لا توجد رسائل بعد
              </Typography>
              <Typography variant="body1" color="text.secondary">
                ابدأ المحادثة بإرسال رسالة للمستخدم
              </Typography>
            </Box>
          )}
          <div ref={messagesEndRef} />
        </Paper>

        {/* Message Input */}
        <Paper sx={{
          p: 3,
          borderRadius: 3,
          boxShadow: '0 8px 24px rgba(0,0,0,0.08)',
          border: '1px solid',
          borderColor: 'divider',
          background: 'white'
        }}>
          <Box component="form" onSubmit={handleSendMessage} sx={{ display: 'flex', gap: 2 }}>
            <TextField
              fullWidth
              variant="outlined"
              placeholder="اكتب رسالتك هنا..."
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              disabled={sending}
              multiline
              maxRows={4}
              sx={{
                '& .MuiOutlinedInput-root': {
                  borderRadius: 3,
                  fontSize: '1rem',
                  '&:hover': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '2px'
                    }
                  },
                  '&.Mui-focused': {
                    '& .MuiOutlinedInput-notchedOutline': {
                      borderColor: 'primary.main',
                      borderWidth: '2px'
                    }
                  }
                }
              }}
            />
            <Button
              type="submit"
              variant="contained"
              disabled={sending || !newMessage.trim()}
              startIcon={sending ? <CircularProgress size={20} /> : <SendIcon />}
              sx={{
                borderRadius: 3,
                px: 4,
                py: 1.5,
                minWidth: 120,
                fontSize: '1rem',
                fontWeight: 600,
                background: 'linear-gradient(135deg, #0ea5e9, #3b82f6)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #0284c7, #2563eb)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 8px 24px rgba(14, 165, 233, 0.4)'
                },
                '&:disabled': {
                  background: 'grey.300',
                  transform: 'none',
                  boxShadow: 'none'
                },
                transition: 'all 0.3s ease'
              }}
            >
              {sending ? 'جاري الإرسال...' : 'إرسال'}
            </Button>
          </Box>
        </Paper>

        {/* ✨ NEW: نافذة منبثقة لعرض الصورة المكبرة */}
        <Dialog
          open={!!selectedImage}
          onClose={() => setSelectedImage(null)}
          maxWidth="lg"
          PaperProps={{
            sx: {
              bgcolor: 'transparent',
              boxShadow: 'none',
              overflow: 'hidden'
            }
          }}
        >
          {selectedImage && (
            <img
              src={selectedImage}
              alt="Full Size"
              style={{
                maxWidth: '100%',
                maxHeight: '90vh',
                borderRadius: '8px',
                boxShadow: '0 20px 40px rgba(0,0,0,0.4)',
                objectFit: 'contain'
              }}
            />
          )}
        </Dialog>
      </Container>
    </motion.div>
  );
}
